import { Button } from '@/components/Button';
import { cn } from '@/lib/cn';

interface TaskResumeSheetProps {
  open: boolean;
  previousTaskTitle: string | null;
  /** Overrides the default “task completed” copy. */
  body?: string;
  resumeLabel?: string;
  dismissLabel?: string;
  onResumePrevious: () => void;
  onChooseDifferent: () => void;
  onClose: () => void;
}

/** Compact centered prompt: not a side sheet. */
export function TaskResumeSheet({
  open,
  previousTaskTitle,
  body,
  resumeLabel,
  dismissLabel = 'Not now',
  onResumePrevious,
  onChooseDifferent,
  onClose,
}: TaskResumeSheetProps) {
  if (!open || !previousTaskTitle) return null;

  return (
    <div className="fixed inset-0 z-[220] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Dismiss"
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Resume previous task"
        className="relative z-10 w-full max-w-sm rounded-xl border border-border bg-surface-1 p-4 shadow-lg"
      >
        <p className="text-body text-text-secondary">
          {body ?? 'Return to what you were working on?'}
        </p>
        <div className="mt-4 flex flex-col gap-2">
          <Button onClick={onResumePrevious}>
            {resumeLabel ?? `Resume ${previousTaskTitle}`}
          </Button>
          <Button variant="secondary" onClick={onChooseDifferent}>
            {dismissLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

interface BacklogAddPickerProps {
  open: boolean;
  onClose: () => void;
  options: { taskId: number; title: string; estimatedMinutes: number }[];
  onPick: (taskId: number, estimatedMinutes: number) => void;
  loading?: boolean;
}

/** Quick list of backlog tasks to pull into the current sprint. */
export function BacklogAddPicker({
  open,
  onClose,
  options,
  onPick,
  loading,
}: BacklogAddPickerProps) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[220] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Dismiss"
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add from backlog"
        className="relative z-10 w-full max-w-sm rounded-xl border border-border bg-surface-1 p-4 shadow-lg"
      >
        <p className="mb-3 text-small font-medium text-text-secondary">Add from backlog</p>
        <ul className="flex max-h-72 flex-col gap-1 overflow-y-auto">
          {options.map((o) => (
            <li key={o.taskId}>
              <button
                type="button"
                disabled={loading}
                className={cn(
                  'pressable flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-left',
                  'hover:bg-surface-2 disabled:opacity-50',
                )}
                onClick={() => onPick(o.taskId, o.estimatedMinutes)}
              >
                <span className="min-w-0 truncate text-body">{o.title}</span>
                <span className="shrink-0 text-label text-text-tertiary">
                  {o.estimatedMinutes}m
                </span>
              </button>
            </li>
          ))}
        </ul>
        {options.length === 0 && (
          <p className="text-small text-text-tertiary">No backlog tasks to add.</p>
        )}
        <Button variant="ghost" className="mt-3 w-full" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
